import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, FormControl, InputLabel, Select, MenuItem,
  Divider, Box, TextField, CircularProgress, Stack,
} from '@mui/material';
import networkApi from '../../pages/Network/services/networkApi';

const CIRCLE_COLORS = ['#4ECDC4', '#FF6B6B', '#45B7D1', '#96CEB4', '#FFEEAD', '#D4A5A5'];

// Shown right after any connection request is sent (Network page search,
// a public profile page, or the event-organiser prompt), regardless of
// whether the receiver ever approves. add_circle_member requires an
// accepted connection, so the chosen circle is stashed via
// networkApi.setPendingCircle() and applied server-side once the request
// is accepted (see respond_to_request in lambda/routes/network.py).
export default function AddToCirclePrompt({ open, onClose, personName, requestId }) {
  const [step, setStep] = useState('confirm'); // 'confirm' | 'pick'
  const [circles, setCircles] = useState([]);
  const [circlesLoading, setCirclesLoading] = useState(false);
  const [circleId, setCircleId] = useState('');
  const [shareHistory, setShareHistory] = useState(false);
  const [historyDays, setHistoryDays] = useState('');
  const [newCircleName, setNewCircleName] = useState('');
  const [creatingCircle, setCreatingCircle] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep('confirm');
    setCircleId('');
    setShareHistory(false);
    setHistoryDays('');
    setNewCircleName('');
  }, [open, requestId]);

  const loadCircles = async () => {
    setCirclesLoading(true);
    try {
      const data = await networkApi.getCircles();
      setCircles(data || []);
    } catch {
      setCircles([]);
    } finally {
      setCirclesLoading(false);
    }
  };

  const handleWantsCircle = () => {
    setStep('pick');
    loadCircles();
  };

  const handleCreateCircle = async () => {
    if (!newCircleName.trim()) return;
    setCreatingCircle(true);
    try {
      const created = await networkApi.createCircle({
        circle_name: newCircleName.trim(),
        color: CIRCLE_COLORS[circles.length % CIRCLE_COLORS.length],
      });
      setCircles(prev => [...prev, created]);
      setCircleId(created.circle_id);
      setNewCircleName('');
    } finally {
      setCreatingCircle(false);
    }
  };

  const handleConfirm = async () => {
    if (!circleId) return;
    setSaving(true);
    try {
      const days = shareHistory && historyDays ? parseInt(historyDays, 10) : null;
      await networkApi.setPendingCircle(requestId, {
        circle_id: circleId,
        zone_level: 1,
        share_history: shareHistory ? 1 : 0,
        history_days: days,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  if (step === 'confirm') {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Add {personName} to a circle?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            You'll be able to organize your connections and share future event photos with them more easily.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Not now</Button>
          <Button variant="contained" onClick={handleWantsCircle}>Yes, add to a circle</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Add {personName} to a circle</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
        {circlesLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : circles.length === 0 ? (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              You don't have any circles yet. Create one to get started.
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                size="small"
                fullWidth
                placeholder="Circle name"
                value={newCircleName}
                onChange={(e) => setNewCircleName(e.target.value)}
              />
              <Button
                variant="outlined"
                disabled={!newCircleName.trim() || creatingCircle}
                onClick={handleCreateCircle}
                sx={{ textTransform: 'none', flexShrink: 0 }}
              >
                {creatingCircle ? <CircularProgress size={18} /> : 'Create'}
              </Button>
            </Stack>
          </Box>
        ) : (
          <FormControl fullWidth size="small">
            <InputLabel>Circle</InputLabel>
            <Select value={circleId} onChange={(e) => setCircleId(e.target.value)} label="Circle">
              {circles.map(c => (
                <MenuItem key={c.circle_id} value={c.circle_id}>{c.circle_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Divider />

        <Box>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
            Share event history?
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Choose which past events shared in this circle they can see.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant={!shareHistory ? 'contained' : 'outlined'}
              onClick={() => { setShareHistory(false); setHistoryDays(''); }}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              New events only
            </Button>
            <Button
              size="small"
              variant={shareHistory ? 'contained' : 'outlined'}
              onClick={() => setShareHistory(true)}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Share history
            </Button>
          </Box>
          {shareHistory && (
            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <TextField
                size="small"
                type="number"
                placeholder="All history"
                label="Past days"
                value={historyDays}
                onChange={(e) => setHistoryDays(e.target.value.replace(/[^0-9]/g, ''))}
                InputProps={{ inputProps: { min: 1 } }}
                sx={{ width: 140 }}
              />
              <Typography variant="caption" color="text.secondary">
                {historyDays
                  ? `Events from the past ${historyDays} day${historyDays === '1' ? '' : 's'}`
                  : 'Leave blank to share all past history'}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button disabled={saving} onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!circleId || saving}
          onClick={handleConfirm}
          sx={{ textTransform: 'none' }}
        >
          {saving ? <CircularProgress size={18} /> : 'Add to Circle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
